// @flow
import { createContext, useContext, useCallback, useMemo } from 'react';
import type {
  BlogConfigType,
  BlokComponentType,
  CountryType,
  LayoutType,
  TestimonialType,
  AuthorType,
  BlogPostType,
  ParsedBlokType,
  CategoryType,
  AutoModalConfigType,
  PhoneInputSettingsType,
  UrlType,
  HeaderType,
  FooterType
} from './types';

export type SeoLinksType = {
  alternates: { href: string, isSoft?: boolean, lang: string, slug: string }[],
  canonical: string,
  genericSlug: string,
  hasCountrySpecificAlternates: boolean,
  next?: string,
  prev?: string
};

export type PageContextType = {
  autoModalConfig?: AutoModalConfigType,
  blogConfig: BlogConfigType,
  categories?: { [string]: BlokComponentType<CategoryType> },
  countPages?: number,
  countries: CountryType[],
  country: CountryType,
  currentPage?: number,
  footer: FooterType,
  header: HeaderType,
  jobsConfig: {
    apply_modal: UrlType,
    apply_text: string
  },
  layout: LayoutType,
  links: SeoLinksType,
  pageBlogs?: BlogPostType[],

  phoneInputSettings?: PhoneInputSettingsType,
  phoneInputSettingsMap?: { [string]: PhoneInputSettingsType },
  profiles?: { [string]: BlokComponentType<AuthorType> },
  rootSlug?: string,
  story: ParsedBlokType,
  testimonials?: { [string]: BlokComponentType<TestimonialType> }
};

export const PageContext = createContext<?PageContextType>();

export const { Provider } = PageContext;

export const EDITOR_VALUES = {
  TESTIMONIAL: 'testimonials',
  PROFILE: 'profiles',
  CATEGORY: 'categories'
};

export function handleEditorValues<T: { [string]: any }>(
  key: string,
  blockContent: string | BlokComponentType<T>
): void | Object | BlokComponentType<T> {
  // Handling dynamic changes within the editor mode of StoryBlok
  const context = useContext(PageContext) || {};
  const values = context[key] || {};
  if (typeof blockContent === 'string') {
    return values[blockContent];
  }
  return blockContent;
}

export function getPrintTime(): (time?: string | number) => string {
  const { blogConfig } = useContext(PageContext) || {};
  return useMemo(() => {
    if (!blogConfig) return (time?: string | number) => (time ? `${time}` : '');
    return (time?: string | number) =>
      blogConfig.read_time_text.replace(/\{time\}/gi, `${time || ''}`);
  }, [blogConfig.read_time_text]);
}

export function getPageContext(): PageContextType {
  return useContext(PageContext) || {};
}

export function getPageLocale(): string {
  const { rootSlug, country = { code: 'ja' } } = useContext(PageContext) || {};
  return (rootSlug || '').split('/')[1] || country.code;
}

export function getPageLocaleListSeparator(): string {
  const locale = getPageLocale();
  if (locale === 'ja') return '\u3001';
  if (locale.indexOf('ch') === 0) return '\uFF0C';
  return ', ';
}

export function getFormatDate(): (datestring: string) => string {
  const locale = getPageLocale();
  return useCallback(
    (datestring) => {
      try {
        return new Date(datestring).toLocaleDateString(locale, {
          month: 'short',
          year: 'numeric',
          day: 'numeric'
        });
      } catch (e) {
        const date = new Date(datestring);
        const year = date.getFullYear();
        const month = `0${date.getMonth() + 1}`.slice(-2);
        const day = `0${date.getDate()}`.slice(-2);
        return `${year}-${month}-${day}`;
      }
    },
    [locale]
  );
}
